import { useTranslation } from '@/hooks/useTranslation';
import { Modal } from './Modal';
import { useCallback, useEffect, useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { useModalContext } from '@/hooks/useModalContext';

import { toast } from 'sonner';
import { LoadingIndicator } from '../common/LoadingIndicator';
import { useUpdateOrganization } from '@/hooks/organization/useUpdateOrganization';
import { useOrganizationStore } from '@/stores/organization';
import { Organization } from '@/types/organization';
import { useGetOrganization } from '@/hooks/organization/useGetOrganization';

const fields: (keyof Organization)[] = [
  'name',
  'billingName',
  'billingAddress',
  'taxNumber',
];

export const UpdateOrganizationSettingsModal = () => {
  const { t } = useTranslation('common');
  const { closeModal } = useModalContext();
  const organizationState = useOrganizationStore((state) => state.organization);
  const [localOrganization, setLocalOrganization] = useState<
    Partial<Organization>
  >({});
  const [errors, setErrors] = useState<
    Partial<Record<keyof Organization, string>>
  >({});

  const { data: organization } = useGetOrganization({
    organizationId: String(organizationState?.id) ?? '',
    enabled: !!organizationState,
  });

  const handleClose = (isSuccess?: boolean) => {
    closeModal('updateOrganizationSettings', isSuccess ?? false);
  };

  const { mutate: updateOrganization, isPending: isUpdatingOrganization } = useUpdateOrganization({
    id: String(organizationState?.id ?? ''),
    onSuccess: () => {
      toast.success(t('organizationUpdated'));
      handleClose(true);
    },
    onError: () => {
      toast.error(t('organizationUpdateFailed'));
    },
  });

  const validateForm = () => {
    const newErrors: Partial<Record<keyof Organization, string>> = {};
    fields.forEach((field) => {
      if (!localOrganization[field]) {
        newErrors[field] = t('fieldRequired');
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (organization && validateForm()) {
      updateOrganization({
        id: organization.id,
        data: localOrganization,
      });
    }
  };

  useEffect(() => {
    if (organization) {
      setLocalOrganization(organization);
    }
  }, [organization]);

  return (
    <Modal modalType="updateOrganizationSettings" title={t('organizationSettings')}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4">
          <div key="name" className="flex flex-col space-y-1.5">
            <Label htmlFor="name">{t('name')}</Label>
            <Input
              id="name"
              value={localOrganization?.name || ''}
              onChange={(e) => setLocalOrganization({ ...localOrganization, name: e.target.value })}
              className={errors?.name ? 'border-red-500' : ''}
            />
            {errors?.name && (
              <p className="text-red-500 text-sm mt-1">{errors?.name}</p>
            )}
          </div>

          <div key="billingName" className="flex flex-col space-y-1.5">
            <Label htmlFor="billingName">{t('billingName')}</Label>
            <Input
              id="billingName"
              value={localOrganization?.billingName || ''}
              onChange={(e) =>
                setLocalOrganization({ ...localOrganization, billingName: e.target.value })
              }
              className={errors?.billingName ? 'border-red-500' : ''}
            />
            {errors?.billingName && (
              <p className="text-red-500 text-sm mt-1">
                {errors?.billingName}
              </p>
            )}
          </div>

          <div key="billingAddress" className="flex flex-col space-y-1.5">
            <Label htmlFor="billingAddress">{t('billingAddress')}</Label>
            <Input
              id="billingAddress"
              value={localOrganization?.billingAddress || ''}
              onChange={(e) =>
                setLocalOrganization({ ...localOrganization, billingAddress: e.target.value })
              }
              className={errors?.billingAddress ? 'border-red-500' : ''}
            />
            {errors?.billingAddress && (
              <p className="text-red-500 text-sm mt-1">
                {errors?.billingAddress}
              </p>
            )}
          </div>

          <div key="taxNumber" className="flex flex-col space-y-1.5">
            <Label htmlFor="taxNumber">{t('taxNumber')}</Label>
            <Input
              id="taxNumber"
              value={localOrganization?.taxNumber || ''}
              onChange={(e) =>
                setLocalOrganization({ ...localOrganization, taxNumber: e.target.value })
              }
              className={errors?.taxNumber ? 'border-red-500' : ''}
            />
            {errors?.taxNumber && (
              <p className="text-red-500 text-sm mt-1">
                {errors?.taxNumber}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => handleClose()}>
            {t('close')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isUpdatingOrganization}
            className="flex gap-1"
            variant="conversionCraft"
          >
            {isUpdatingOrganization && <LoadingIndicator size="small" />}
            {t('save')}
          </Button>
        </DialogFooter>
      </div>
    </Modal>
  );
};
